import {Component, OnInit} from '@angular/core';
import {Observable, Subject} from 'rxjs';
import {debounceTime} from 'rxjs/operators';
import {
  OrganizationInformationRestService
} from '../../shared/services/nso-data-portal/rest-services/organization-information-rest.service';
import {
  OrganizationData,
  RegionData,
  SimpleEntityData,
  WorldData
} from '../../shared/types/nso-data-portal/entity-data';
import {SecurityService} from '../../shared/services/security/security.service';
import {GlobalMessagesService} from '../../shared/services/messages/global.messages.service';
import {TranslateService} from '@ngx-translate/core';
import {NdpSearchService} from '../../shared/services/nso-data-portal/rest-services/ndp-search.service';
import {SearchEntityLevels} from '../../shared/types/nso-data-portal/search-entity-levels';
import {EntityInformationRestService} from '../../shared/services/nso-data-portal/rest-services/entity-information-rest.service';
import {ActivatedRoute} from '@angular/router';
import {PersonRestService} from '../../shared/services/person/person-rest.service';
import * as _ from 'lodash';

@Component({
  selector: 'wsd-dashboard-data-portal',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css']
})
export class DashboardComponent implements OnInit {

  searchChanged = new Subject<void>();

  entityList: Observable<SimpleEntityData[]>;

  query: string;

  showDocuments: boolean = false;
  showInteraction: boolean = false;
  showCensus: boolean = false;
  showGroupAdminView: boolean = false;

  entity: OrganizationData & RegionData & WorldData;

  entityLevel: SearchEntityLevels;

  searchEntityLevels = SearchEntityLevels;

  hasPolicyDocumentsPermission: boolean = false;

  organizationId: number;

  showSearch: boolean = false;

  allowedSearchRoleIds = [12, 13, 14, 16, 17];

  constructor(private securityService: SecurityService,
              private translate: TranslateService,
              private notify: GlobalMessagesService,
              private organizationService: OrganizationInformationRestService,
              private ndpSearchService: NdpSearchService,
              private entityInformationRestService: EntityInformationRestService,
              private route: ActivatedRoute,
              private personService: PersonRestService) {
  }

  ngOnInit() {
    this.route.params.subscribe(params => {
      this.organizationId = +params['id'];
      if (this.organizationId != null) {
        this.fetchEntity({
          id: this.organizationId,
          level: SearchEntityLevels.ORGANIZATION
        });
        return;
      }
    });
    this.searchChanged.pipe(
      debounceTime(500)
    ).subscribe(() => this.load());

    if (!this.organizationId) {
      this.loadDefault();
    }
    this.securityService.hasPermission('NDP_ORGANIZATION_POLICY_DOCUMENTS_READ').subscribe(
      hasPermission => this.hasPolicyDocumentsPermission = hasPermission
    );

    this.securityService.getLoggedUser().subscribe(user => {
      if (user.consultantId) {
        this.personService.getPersonFromAllPersons(user.consultantId).subscribe(person => {
          if (person.roles) {
            const hasAllowedRole = _.some(person.roles, (role) =>
              this.allowedSearchRoleIds.includes(role.id));
            if (hasAllowedRole) {
              this.showSearch = true;
            }
          }
        });
      }
    });
  }

  private loadDefault() {
    let savedOrganizationId;
    if (localStorage.getItem('portal_orgId') !== null) {
      const localStorageEntityData = JSON.parse(localStorage.getItem('portal_orgId'));
      savedOrganizationId = localStorageEntityData.entity;
      this.entityLevel = localStorageEntityData.entityLevel;
    }


    if (savedOrganizationId) {
      this.fetchEntity({
        id: savedOrganizationId,
        level: this.entityLevel,
      });
    } else {
      this.securityService.getLoggedUser().subscribe(
        user => user.organization && this.fetchEntity({
          id: user.organization.id,
          level: SearchEntityLevels.ORGANIZATION,
        })
      );
    }
  }

  private load() {
    this.entityList = this.ndpSearchService.searchAllNdpEntities(this.query);
  }

  search(event) {
    this.query = event.query;
    this.searchChanged.next();
  }

  fetchEntity(event) {
    this.entityLevel = event.level;
    this.entityInformationRestService.getEntityInformation(event).subscribe(
      (res) => {
        this.securityService.getLoggedUser().subscribe(user => {
          this.entity = res;
          if (user.organization != null) {
            this.showGroupAdminView = user.organization.id === this.entity.id;
          }
        });
      });
    this.hideDetails();
  }

  private hideDetails() {
    this.showDocuments = false;
    this.showInteraction = false;
    this.showCensus = false;
  }

  changeFollowStatus() {
    this.organizationService.changeFollowStatus(this.entity.id).subscribe(
      () => {
        this.entity.followedByUser = !this.entity.followedByUser;
        this.notify.showSuccessMessage(
          this.translate.instant(
            this.entity.followedByUser ?
              'DATA_PORTAL.FOLLOW_MESSAGE' : 'DATA_PORTAL.UNFOLLOW_MESSAGE',
            {name: this.entity.name}
          )
        );
      }
    );
  }

  scrollTop() {
    window.scrollTo(0, 0);
  }

  get detailVisible() {
    return this.showDocuments || this.showInteraction || this.showCensus;
  }

  updateOrganizationContacts() {
    this.organizationService.updateOrganizationContacts(this.entity.id).subscribe(() =>
      this.fetchEntity({
        level: SearchEntityLevels.ORGANIZATION,
        id: this.entity.id
      }));
  }
}
